// @flow

import * as React from 'react';
import classNames from '@ta/common.classnames';
import { ClickTracker } from '@ta/common.tracking';
import SocialTab from './SocialTab';

import styles from './SocialTabs.less';

export type Props = {
  // $FlowFixMe92
  children: Array<SocialTab>,
  useFeedStyle?: boolean,
  flexPanel?: boolean,
  tabNames?: Array<string>,
  activeTab?: string,

  /** We will render non-active tabs (but will not be displayed) */
  renderNonActiveTabs?: boolean,
};

type State = {
  tabState: Array<boolean>,
};

/**
 * This Context is used when renderNonActiveTabs is set to true, so all the tab content
 * will be rendered on page, and this will let the children components in the tabs know
 * if they are the current active tab or not.
 */
// $FlowFixMe
export const TabActiveContext = React.createContext({ isActive: true, index: 0 });

export default class SocialTabs extends React.Component<Props, State> {
  static defaultProps = {
    useFeedStyle: false,
    flexPanel: false,
    tabNames: [],
    activeTab: '',
    renderNonActiveTabs: false,
  };

  constructor(props: Props) {
    super(props);

    const tabState = React.Children.map(this.props.children, item => item?.props.isActive);

    this.state = {
      tabState,
    };

    this.currentTabIndex = 0;
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.props.activeTab &&
      this.props.activeTab !== prevProps.activeTab &&
      this.props.tabNames &&
      this.props.tabNames.length === this.state.tabState.length
    ) {
      const index = this.props.tabNames.findIndex(name => name === this.props.activeTab);
      this.onTabChange(index, () => {});
    }
  }

  onTabChange(tabIndex: number, originalOnClick: () => void) {
    if (tabIndex === this.currentTabIndex) {
      return;
    }

    // Update each tab's state by comparing the currently selected index with each tab's index
    const newTabState = React.Children.map(
      this.props.children,
      (item, index) => index === tabIndex,
    );

    this.setState(
      {
        tabState: newTabState,
      },
      originalOnClick,
    );
  }

  currentTabIndex: number;

  keyOffset: number = Math.random();

  renderActiveTabContent() {
    // Find the first tab that is active (set to true)
    this.currentTabIndex = this.state.tabState.findIndex(x => x);

    return this.props.children[this.currentTabIndex].props.children || null;
  }

  renderAllTabContent() {
    // Find the first tab that is active (set to true)
    this.currentTabIndex = this.state.tabState.findIndex(x => x);
    return (
      <React.Fragment>
        {this.state.tabState.map((tabOn, index) => (
          <TabActiveContext.Provider
            key={`tab_${this.keyOffset + index}`}
            value={{ isActive: tabOn, index }}
          >
            <div className={classNames({ [styles.inactive]: !tabOn })}>
              {this.props.children[index].props.children || null}
            </div>
          </TabActiveContext.Provider>
        ))}
      </React.Fragment>
    );
  }

  renderTabComponents() {
    return React.Children.map(
      this.props.children,
      (item, index) =>
        item && (
          <ClickTracker {...item.props.trackingProps} forceAjax useSpan>
            <SocialTab
              {...item.props}
              isActive={this.state.tabState[index]}
              onClick={() => this.onTabChange(index, item.props.onClick)}
            >
              {item.props.children}
            </SocialTab>
          </ClickTracker>
        ),
    );
  }

  render() {
    return (
      <React.Fragment>
        <ul
          className={classNames('ui_tabs', 'social_tabs', styles.tabs, {
            [styles.feed_style]: this.props.useFeedStyle,
          })}
        >
          {this.renderTabComponents()}
        </ul>
        <div className={classNames('tab-panel', { [styles.flex_panel]: this.props.flexPanel })}>
          {this.props.renderNonActiveTabs
            ? this.renderAllTabContent()
            : this.renderActiveTabContent()}
        </div>
      </React.Fragment>
    );
  }
}
